import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    CalendarDaysIcon,
    ClockIcon,
    UserIcon,
    UsersIcon,
    PaperClipIcon,
} from '@heroicons/react/24/outline';
import { AcademicCapIcon } from '@heroicons/react/24/solid';
import { PencilSquareIcon } from '@heroicons/react/20/solid';
import { format } from 'date-fns';
import { Button } from '../../../components/buttons/Button';
import { PersonSummary } from '../../../components/avatar';
import { urlFlag } from '../../../common/urlBase';
import {
    EVENT_KINDS,
    courseBannerUrl,
    courseLabelDisplay,
    formatCourseDateRange,
    formatSessionDateTime,
    formatSessionDuration,
    teacherName,
} from './calendarEvents';
import { capitalizarPalabras } from '../../../common/upperCaseWord';
import {
    TASK_PRIORITY_LABELS,
    TASK_WINDOW_LABELS,
    priorityBadgeClass,
    windowBadgeClass,
} from '../tasks/taskLabels';
import { EventPopoverMetaItem } from './components/EventPopoverMetaItem';
import { CourseStatusBadge } from '../components/CourseStatusBadge';
import { isStudentCourseEntryPaid } from '../../../constants/courseEntry';

const GAP = 10;
const VIEWPORT_PAD = 12;

const longDate = (date) => formatSessionDateTime(date);

const placePopover = (anchorRect, popoverEl) => {
    const width = popoverEl.offsetWidth;
    const height = popoverEl.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = anchorRect.right + GAP;
    if (left + width > vw - VIEWPORT_PAD) {
        left = anchorRect.left - width - GAP;
    }
    if (left < VIEWPORT_PAD) {
        left = Math.max(VIEWPORT_PAD, (vw - width) / 2);
    }

    let top = anchorRect.top;
    if (top + height > vh - VIEWPORT_PAD) {
        top = vh - height - VIEWPORT_PAD;
    }
    if (top < VIEWPORT_PAD) top = VIEWPORT_PAD;

    return { top, left };
};

/**
 * Ficha de detalle junto al evento. Vive en un portal para no recortarse con
 * el overflow del calendario. El puntero puede pasar del evento a la ficha
 * sin que se cierre.
 */
export const EventHoverPopover = ({
    event,
    anchorRect,
    onClose,
    onKeepOpen,
    onDismiss,
    onEditCourse,
    onOpenTask,
}) => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.user);
    const cardRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });

    const isStudent = user?.typeUser === 'estudiante';
    const isEnterDisabled = !isStudentCourseEntryPaid(user);

    const { kind, course, classSession, task } = event?.resource || {};
    const isTask = kind === EVENT_KINDS.TASK;
    const bannerUrl = courseBannerUrl(course);
    const professor = teacherName(course?.teacher);
    const courseDates = formatCourseDateRange(course);
    const languageFlagUrl = course?.language?.path
        ? `${urlFlag}${course.language.path}`
        : null;
    const scheduleLabel = classSession?.timeSlot
        || `${format(event.start, 'HH:mm')} – ${format(event.end, 'HH:mm')}`;
    const sessionDuration = formatSessionDuration(event.start, event.end);

    useLayoutEffect(() => {
        if (!anchorRect || !cardRef.current) return;
        setCoords({ ...placePopover(anchorRect, cardRef.current), ready: true });
    }, [anchorRect, event?.id]);

    useEffect(() => {
        const hide = () => (onDismiss ? onDismiss() : onClose());
        const onKey = (nativeEvent) => {
            if (nativeEvent.key === 'Escape') hide();
        };
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose, onDismiss]);

    const goToCourse = () => {
        onDismiss?.();
        navigate(`/curso/${course?._id}`);
    };

    const handleEditCourse = () => {
        if (!course?._id || !onEditCourse) return;
        onDismiss?.();
        onEditCourse(course._id);
    };

    if (!event || !anchorRect) return null;

    const popoverTitle = isTask
        ? capitalizarPalabras(event.title)
        : courseLabelDisplay(course);
    const teacherEmail = course?.teacher?.email?.trim() || '';
    const canStudentSubmit = isTask && isStudent
        && task
        && !task.submitted
        && task.windowState === 'active';
    const canOpenTask = isTask && onOpenTask && task?._id && course?._id;

    const handleOpenTask = () => {
        if (!canOpenTask) return;
        onDismiss?.();
        onOpenTask(task._id, course._id);
    };

    return createPortal(
        <div
            ref={cardRef}
            role="dialog"
            aria-label={popoverTitle}
            aria-modal="false"
            onMouseEnter={onKeepOpen}
            onMouseLeave={onClose}
            className="cal-event-popover pointer-events-auto"
            style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 80,
                opacity: coords.ready ? 1 : 0,
                transform: coords.ready ? 'translateY(0)' : 'translateY(4px)',
                transition: 'opacity 180ms cubic-bezier(.23, 1, .32, 1), transform 180ms cubic-bezier(.23, 1, .32, 1)',
            }}
        >
            <div className="cal-event-popover__shell">
                {!isTask && bannerUrl ? (
                    <div className="cal-event-popover__hero">
                        <img
                            src={bannerUrl}
                            alt=""
                            className="cal-event-popover__hero-img"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                ) : null}

                <div className="cal-event-popover__body">
                    <div className="cal-event-popover__intro">
                        <div className="cal-event-popover__title-row">
                            <h3 className="cal-event-popover__title">{popoverTitle}</h3>
                            {!isTask && course?.status ? (
                                <CourseStatusBadge
                                    status={course.status}
                                    className="cal-event-popover__status"
                                />
                            ) : null}
                        </div>
                        {course?.identifier ? (
                            <div className="cal-event-popover__course-line">
                                {languageFlagUrl ? (
                                    <img
                                        src={languageFlagUrl}
                                        alt=""
                                        className="cal-event-popover__flag"
                                        width={28}
                                        height={20}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : null}
                                <p className="cal-event-popover__subtitle" translate="no">
                                    {course.identifier}
                                </p>
                            </div>
                        ) : null}
                    </div>

                    {professor && !isTask ? (
                        <PersonSummary
                            variant="inline"
                            className="cal-event-popover__teacher"
                            firstName={course?.teacher?.firstName}
                            lastName={course?.teacher?.lastName}
                            name={professor}
                            role="Profesor"
                            meta={teacherEmail}
                            avatarSize="md"
                        />
                    ) : null}

                    {isTask ? (
                        <div className="cal-event-popover__section cal-event-popover__section--task">
                            {course?.identifier ? (
                                <p className="cal-event-popover__task-course" translate="no">
                                    {course.identifier}
                                </p>
                            ) : null}
                            <div className="cal-event-popover__badges">
                                <span className={windowBadgeClass(task.windowState)}>
                                    {TASK_WINDOW_LABELS[task.windowState]}
                                </span>
                                <span className={priorityBadgeClass(task.priority)}>
                                    {TASK_PRIORITY_LABELS[task.priority]}
                                </span>
                            </div>
                            {task.description ? (
                                <p className="cal-event-popover__description">{task.description}</p>
                            ) : null}
                            {task.windowState === 'upcoming' && task.activationDate ? (
                                <EventPopoverMetaItem icon={ClockIcon} label="Se activa">
                                    {longDate(task.activationDate)}
                                </EventPopoverMetaItem>
                            ) : null}
                            <EventPopoverMetaItem icon={CalendarDaysIcon} label="Fecha límite">
                                {longDate(task.dueDate)}
                            </EventPopoverMetaItem>
                            {task.teacher ? (
                                <EventPopoverMetaItem icon={UserIcon} label="Profesor">
                                    <span className="capitalize">{teacherName(task.teacher)}</span>
                                </EventPopoverMetaItem>
                            ) : null}
                            {typeof task.submittedCount === 'number' ? (
                                <EventPopoverMetaItem icon={UsersIcon} label="Entregas">
                                    {task.submittedCount} / {task.totalStudents}
                                </EventPopoverMetaItem>
                            ) : null}
                            {task.attachment?.url ? (
                                <EventPopoverMetaItem icon={PaperClipIcon} label="Adjunto">
                                    <a
                                        href={task.attachment.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-indigo-700 hover:underline break-all"
                                    >
                                        {task.attachment.fileName}
                                    </a>
                                </EventPopoverMetaItem>
                            ) : null}
                            {task.submitted ? (
                                <EventPopoverMetaItem icon={ClockIcon} label="Entrega">
                                    Tarea entregada
                                </EventPopoverMetaItem>
                            ) : null}
                            {task.graded ? (
                                <EventPopoverMetaItem icon={ClockIcon} label="Calificación">
                                    Tarea calificada{Number.isInteger(task.grade) ? ` · ${task.grade}/10` : ''}
                                </EventPopoverMetaItem>
                            ) : null}
                        </div>
                    ) : (
                        <div className="cal-event-popover__section cal-event-popover__grid">
                            {courseDates ? (
                                <EventPopoverMetaItem icon={CalendarDaysIcon} label="Duración del curso">
                                    {courseDates}
                                </EventPopoverMetaItem>
                            ) : null}
                            <EventPopoverMetaItem icon={CalendarDaysIcon} label="Esta sesión">
                                {longDate(event.start)}
                            </EventPopoverMetaItem>
                            <EventPopoverMetaItem icon={ClockIcon} label="Horario">
                                {scheduleLabel}
                            </EventPopoverMetaItem>
                            {sessionDuration ? (
                                <EventPopoverMetaItem icon={ClockIcon} label="Duración de la sesión">
                                    {sessionDuration}
                                </EventPopoverMetaItem>
                            ) : null}
                            {course?.totalStudents > 0 ? (
                                <EventPopoverMetaItem icon={UsersIcon} label="Alumnos inscritos">
                                    {course.totalStudents}
                                </EventPopoverMetaItem>
                            ) : null}
                        </div>
                    )}

                    {isTask && canOpenTask ? (
                        <div className="cal-event-popover__actions">
                            {canStudentSubmit ? (
                                <Button
                                    variant="gradient"
                                    className="cal-event-popover__btn cal-event-popover__btn--enter"
                                    onClick={handleOpenTask}
                                >
                                    Entregar tarea
                                </Button>
                            ) : (
                                <Button
                                    variant="outline"
                                    className="cal-event-popover__btn"
                                    onClick={handleOpenTask}
                                >
                                    Ver tarea
                                </Button>
                            )}
                        </div>
                    ) : null}

                    {!isTask && course?._id ? (
                        <div className="cal-event-popover__actions">
                            {onEditCourse ? (
                                <Button
                                    variant="drawerActions"
                                    icon={<PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />}
                                    className="cal-event-popover__btn cal-event-popover__btn--edit"
                                    onClick={handleEditCourse}
                                >
                                    Editar
                                </Button>
                            ) : null}
                            <Button
                                variant="gradient"
                                disabled={isEnterDisabled}
                                title={isEnterDisabled ? 'Tu suscripción no está pagada' : undefined}
                                icon={<AcademicCapIcon className="h-3.5 w-3.5" aria-hidden="true" />}
                                className="cal-event-popover__btn cal-event-popover__btn--enter disabled:from-slate-400 disabled:to-slate-500"
                                onClick={goToCourse}
                            >
                                Entrar
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>,
        document.body,
    );
};
